/**
 * to navigate through application routes use
 * @example: app.navigate('landingpage', {optional} options);
 */

import app from 'app';

const FONT_REGULAR = 'Regular';
const FONT_BOLD = 'Bold';
const FONT_ITALIC = 'Italic';
const FONT_BOLD_ITALIC = 'BoldItalic';

app.namespace.Controller.FontController = class {
  /** @namespace font.defaultFont */
  /** @namespace font.databaseIdentifier */
  constructor(fonts) {
    this.generateFontList(fonts);
    this.generateFontDictionary(fonts);
    this.fontFormats = this.getFontFormats(fonts);
    this.generateCss(fonts);
  }

  find(predicate) {
    return _.find(this.fontList, predicate);
  }

  findByName(fontName) {
    return this.find({ [FONT_REGULAR]: { fontFamily: fontName } });
  }

  generateFontList(fonts) {
    this.fontList = [];
    fonts.forEach((font) => {
      const o = { default: font.defaultFont };

      o[FONT_REGULAR] = this.generateFontListEntry(font);
      if (font.defaultFont) {
        o[FONT_BOLD] = this.generateFontListEntry(font, FONT_BOLD);
        o[FONT_ITALIC] = this.generateFontListEntry(font, FONT_ITALIC);
        o[FONT_BOLD_ITALIC] = this.generateFontListEntry(
          font,
          FONT_BOLD_ITALIC
        );
      }

      this.fontList.push(o);
    });
  }

  generateFontListEntry(font, suffix) {
    return {
      fontFamily: _.isEmpty(suffix)
        ? font.fontFamily
        : this.changeFontSuffix(font.fontFamily, suffix),
      srcUrl: _.isEmpty(suffix)
        ? font.srcUrl
        : this.changeFontSuffix(font.srcUrl, suffix + '.' + font.format),
      text: font.applyStyleText,
      rtlDirection: !!font.defaultRTLDirection,
      format: font.format,
      fontWeight: font.fontWeight,
      fontStyle: font.fontStyle,
    };
  }

  generateFontDictionary(fonts) {
    this.fontsByFamily = {};
    fonts.forEach((font) => {
      const regularFontFamily = font.fontFamily;
      const boldFontFamily = this.changeFontSuffix(
        regularFontFamily,
        FONT_BOLD
      );
      const italicFontFamily = this.changeFontSuffix(
        regularFontFamily,
        FONT_ITALIC
      );
      const boldItalicFontFamily = this.changeFontSuffix(
        regularFontFamily,
        FONT_BOLD_ITALIC
      );

      if (font.defaultFont) {
        this.fontsByFamily[regularFontFamily] =
          this.generateFontDictionaryEntry(font, 'R');
        this.fontsByFamily[boldFontFamily] = this.generateFontDictionaryEntry(
          font,
          'B'
        );
        this.fontsByFamily[italicFontFamily] = this.generateFontDictionaryEntry(
          font,
          'I'
        );
        this.fontsByFamily[boldItalicFontFamily] =
          this.generateFontDictionaryEntry(font, 'BI');
      } else {
        this.fontsByFamily[regularFontFamily] =
          this.generateFontDictionaryEntry(font);
      }
    });
  }

  generateFontDictionaryEntry(font, suffix) {
    const o = {
      isDefault: font.defaultFont,
      regular: font.fontFamily,
      text: font.applyStyleText,
    };

    if (!suffix) {
      return Object.assign(
        {
          vizName: font.fontFamily,
        },
        o
      );
    }

    return Object.assign(
      {
        vizName: this.changeFontSuffix(font.fontFamily, suffix),
      },
      o
    );
  }

  getFontFormats(fonts) {
    const singleEntry = (font) =>
      `${font.friendlyName}=${font.fontFamily}=${font.defaultFont}`;
    return fonts.map(singleEntry).join(';');
  }

  changeFontSuffix(font, suffix) {
    return font.slice(0, font.lastIndexOf('-')) + '-' + suffix;
  }

  isDefaultFont(family) {
    if (!this.fontsByFamily[family]) {
      return null;
    }
    return this.fontsByFamily[family].isDefault;
  }

  getApplyText(family) {
    if (!this.fontsByFamily[family]) {
      return '';
    }
    return this.fontsByFamily[family].text;
  }

  getRegularFontFamily(family) {
    if (!this.fontsByFamily[family]) {
      return family;
    }
    return this.fontsByFamily[family].regular;
  }

  getVizName(family) {
    if (!this.fontsByFamily[family]) {
      return '';
    }
    return this.fontsByFamily[family].vizName;
  }

  generateCss(fonts) {
    const fontTemplate = app.compileTemplate.$('#fontsCss-tmpl');
    const fontsHash = CryptoJS.MD5(
      JSON.stringify(this.fontsByFamily)
    ).toString();

    if (!fonts.isCache) {
      if (!$('#fontsCss.' + fontsHash).length) {
        $('head').append(
          $(fontTemplate({ fonts: this.fontList })).addClass(fontsHash)
        );
        this.loadFonts();
      }
    }
  }

  loadFonts() {
    if (window.WebFont) {
      window.WebFont.load({
        classes: false,
        custom: {
          families: Object.keys(this.fontsByFamily),
        },
      });
    }
  }
};

export default app.namespace.Controller.FontController;
